import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {distinctUntilChanged, filter, first, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {can} from '../../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {EnergyCommunity, EntityType} from 'smag-commons/cfg/hierarchy';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {SmagApiService} from '../../../api/smag-api.service';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {isDefined} from '../../../commons/commons';
import {ConfigurationService} from '../../../shared/configuration.service';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ECEconomicsSummaryMonth, ECO_ERROR_PARAM_KEY, EconomicsErrorType} from 'smag-commons/dto/eco';
import {
    ChartOptions,
    disableDataLabels,
    disableLegend,
    getChartWithOffsetYAndHeight,
    getDonutWithSize,
    getDonutWithSizeAndOffsetX,
    getNiceTooltip,
    getPieWithOffsetX,
    getTranslatedLabels,
    isFutureDate,
    isViewable,
    isWholeYearSelected,
    keepYourSecrets,
    mkBillingPeriodParams,
    MONTHS,
    sanityFromTimeActivationDate,
    sanityToTime,
    showNullData,
    SortDirection
} from '../../../shared/ec-economics';
import {ECEconomicsPerformance, ECEconomicsSummaryResponse, ECMonthStatus} from 'smag-commons/dto/eco/community';
import {ApexLegend, ApexTitleSubtitle} from 'ng-apexcharts';
import {environment} from '../../../../environments/environment';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {I18NService} from '../../../i18n/i18n.service';
import {COSTS, EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {ComputationStatusService} from '../../../shared/compute-status.service';

enum SortCriterion {
    MONTH = 'month',
    NET_REVENUE = 'net_revenue',
    MEMBER_ABSORPTION_REVENUE = 'member_absorption_revenue',
    MEMBER_PLANT_REVENUE = 'member_plant_revenue',
    THIRD_PARTY_PRODUCER_REVENUE = 'third_party_producer_revenue'
}

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.MONTH, SortDirection.ASC],
    [SortCriterion.NET_REVENUE, SortDirection.DESC],
    [SortCriterion.MEMBER_ABSORPTION_REVENUE, SortDirection.DESC],
    [SortCriterion.MEMBER_PLANT_REVENUE, SortDirection.DESC],
    [SortCriterion.THIRD_PARTY_PRODUCER_REVENUE, SortDirection.DESC]
];


const DIALOG_MAX_WIDTH = 1000;
const DIALOG_MIN_WIDTH = 700;
const HEADER_COLUMNS = ['month', 'net_revenue', 'member_absorption_revenue', 'member_plant_revenue', 'third_party_producer_revenue', 'consolidated'];
// TODO: removed 'status'


const USE_FILTER = true;

@Component({
    selector: 'app-economics-totals-summary',
    templateUrl: './ec-economics-totals-summary.component.html',
    styleUrls: ['./ec-economics-totals-summary.component.scss']
})
export class EcEconomicsTotalsSummaryComponent implements OnInit, OnDestroy {

    private readonly nvp: NiceValuePipe;
    private destroyed = new Subject<void>();
    private sortCriteria: [SortCriterion, SortDirection][] = [...DEFAULT_SORT_CRITERIA];
    private timeZone: string;
    private billingPeriodSubj = new ReplaySubject<BillingPeriod>(1);
    private futureEc = false;
    private reload = new BehaviorSubject<void>(undefined);
    private reloadEcoTotalsData = new Subject<void>();

    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;
    @ViewChild('confirmStatus') private confirmStatusDialog: TemplateRef<any>;

    // consolidatedForm: FormGroup; -> currently hidden
    hierarchyEntity: Observable<HierarchyEntity> = null;
    summary: ECEconomicsSummaryResponse = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    inactiveEc = false;
    // isConfirmDisabled = true; -> currently not used
    templateIsDownloading = false;
    incentivesAndAbsorptionsChart: Partial<ChartOptions>;
    expensesAndRevenueChart: Partial<ChartOptions>;
    revenueDistributionChart: Partial<ChartOptions>;
    membersRedistributionChart: Partial<ChartOptions>;
    sharedFromAbsorption: Partial<ChartOptions>;
    sharedFromInjection: Partial<ChartOptions>;
    currentYear: number = null;
    now: Moment = null;
    communityNotConfigured = false;
    isWholeYear = false;
    showComputationWarning = false;

    readonly USE_FILTER = USE_FILTER;
    readonly TABLE_COLUMNS = HEADER_COLUMNS;
    readonly isViewable = isViewable;
    readonly SortCriterion = SortCriterion;


    constructor(private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly router: Router,
                private readonly dialog: MatDialog,
                private readonly i18n: I18NService,
                private readonly computationService: ComputationStatusService) {
        // this.consolidatedForm = this.fb.group({}); -> currently hidden
        this.nvp = new NiceValuePipe(i18n);
    }

    ngOnInit(): void {
        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => {
                this.timeZone = timeCfg.time_zone;
                this.now = moment.tz(this.timeZone);
            });

        this.computationService.showComputingWarning
            .pipe(takeUntil(this.destroyed))
            .subscribe(bool => this.showComputationWarning = bool);

        // time range is stored in URL, avoid "navigation" to same entity when setting URL parameters for time range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });

        combineLatest([this.hierarchyEntity, this.billingPeriodSubj, this.reload])
            .pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity, billingPeriod]) => {
                this.billingPeriod = billingPeriod;
                this.currentYear = moment.tz(billingPeriod.fromTime, this.timeZone).year();
                const orderBy: string[] = this.sortCriteria.map(([criterion, direction]) => `${criterion}_${direction}`);
                if (hierarchyEntity?.type === EntityType.ENERGY_COMMUNITY && this.isViewable(hierarchyEntity)) {
                    this.summary = null;
                    if (!this.inactiveEc && !this.futureEc) {
                        this.reloadEcoTotalsData.next();
                        this.computationService.checkComputationStatus(hierarchyEntity.id,
                            moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year())
                            .pipe(takeUntil(merge(this.destroyed, this.reloadEcoTotalsData)))
                            .subscribe(() => {
                                this.apiService.ecEconomicsSummaryResponse(hierarchyEntity.id,
                                    sanityFromTimeActivationDate(billingPeriod.fromTime, this.billingPeriodCfg.pastDate, this.timeZone),
                                    sanityToTime(this.timeZone, billingPeriod.toTime), orderBy) // do not use sanityToTime, the whole year is needed
                                    .subscribe(summary => {
                                        this.summary = summary;
                                        this.communityNotConfigured = (this.summary.errors || [])
                                            .filter(err => err.type === EconomicsErrorType.MISSING_PARAM)
                                            .some(err =>
                                                err.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
                                                err.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
                                                err.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
                                        this.mkCharts(this.nvp);
                                        // consolidatedForm is currently hidden
                                        // summary.months?.forEach(month => {
                                        //     if (!month.consolidated) {
                                        //         this.consolidatedForm.addControl(`${month.month}_consolidated`, new FormControl(false));
                                        //     }
                                        // });
                                    });
                            });
                    } else {
                        this.generateInactiveEcoSummaryData();
                        this.mkCharts(this.nvp);
                    }
                }
            });

        // consolidatedForm is currently hidden
        // this.consolidatedForm.valueChanges
        //     .pipe(takeUntil(this.destroyed))
        //     .subscribe(values => {
        //         this.isConfirmDisabled = !this.summary.months
        //             .filter(month => !month.consolidated && this.isMonthConsolidatable(month.month))
        //             .map(month => values[`${month.month}_consolidated`] as boolean)
        //             .some(value => value);
        //     });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.isWholeYear = isWholeYearSelected(period.fromTime, period.toTime, this.timeZone, period.type);
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    monthIndexToName(index: number) {
        return MONTHS[index - 1];
    }

    ongoingMonth(index: number) {
        const year = moment.tz(this.billingPeriod.fromTime, this.timeZone).year();
        return this.isWholeYear && year === this.now.year() && this.now.month() <= index - 1;
    }

    // isMonthConsolidatable(month: number) {
    // TODO add controls:
    //  TIP (CER and PV)
    //  GSE community costs
    //  Energy withdrawal

    // const currentMonthFormControl = this.consolidatedForm.controls[`${month}_consolidated`];
    // const currentMonth = this.summary.months.find(x => x.month === month);
    // // previous month must be consolidated, or it must have been flagged in the form
    // const isPreviousFormMonthConsolidated = this.consolidatedForm.controls[`${month - 1}_consolidated`]?.value as boolean;
    // const isPreviousResponseMonthConsolidated = this.summary.months.find(x => x.month === month - 1)?.consolidated;
    // const isPreviousMonthFlagged = isPreviousFormMonthConsolidated || isPreviousResponseMonthConsolidated || false;
    //
    // if (!isPreviousMonthFlagged && currentMonthFormControl.value === true) {
    //     currentMonthFormControl.setValue(false);
    // }
    //
    // return isPreviousMonthFlagged &&
    //     currentMonth?.status === ECMonthStatus.GSE_UPLOADED; // TODO && more
    // return false; // FIXME currently not supported by BE
    // }

    // onConfirmStatus() {
    // FIXME currently not supported by BE
    // this.dialog.open(this.confirmStatusDialog, {
    //     maxWidth: `${DIALOG_MAX_WIDTH}px`,
    //     hasBackdrop: true
    // }).afterClosed().subscribe(confirm => {
    //     if (confirm) {
    //         const monthsToConsolidate = this.summary.months
    //             .filter(month => !month.consolidated && this.isMonthConsolidatable(month.month))
    //             .map(month => [month.month,
    //                 this.consolidatedForm.controls[`${month.month}_consolidated`].value] as [number, boolean])
    //             .filter(([month, toConsolidate]) => toConsolidate);
    //         // TODO
    //         console.debug(monthsToConsolidate);
    //         // call api to consolidate months
    //         // this.apiService...
    //         //     .toPromise()
    //         //     .then(() => {
    //         //             this.msgBoxService.info(this.i18n.translate('Status confirmed correctly'));
    //         //         }
    //         //     ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while trying to confirm the status'), err));
    //     }
    // });
    // }

    onClickSortBy(criterion: SortCriterion) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, this.invertSortDirection(criterion)],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        this.reload.next();
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    invertSortDirection(criterion: SortCriterion) {
        return this.getSortDirection(criterion) === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
    }

    getSortIconName(criterion: SortCriterion): string {
        return this.getSortDirection(criterion) === SortDirection.DESC ? 'fal-sort-amount-down' : 'fal-sort-amount-up';
    }

    getStatusIcon(status: ECMonthStatus) {
        switch (status) {
            case ECMonthStatus.GSE_UPLOADED:
                return 'fal-check-circle';
            case ECMonthStatus.GSE_NOT_UPLOADED:
                return 'fal-file-excel';
            case ECMonthStatus.ONGOING:
                return 'fal-clock';
            default:
                return 'fal-question-circle';
        }
    }

    exportEconomicsToExcel() {
        // TODO
    }

    getCellValueTextStyle(status: ECMonthStatus) {
        return status === ECMonthStatus.ONGOING ? 'txt-s-14-7' : 'txt-h-14-7';
    }

    showCellValue(month: number) {
        return !isFutureDate(month - 1, this.currentYear, this.now); // month is one based
    }

    get showNullData() {
        return showNullData(this.inactiveEc, this.futureEc);
    }

    get keepYourSecrets() {
        return keepYourSecrets(this.inactiveEc, this.futureEc, this.communityNotConfigured);
    }

    private showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DIALOG_MAX_WIDTH}px`,
            minWidth: `${DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    private mkCharts(nvp: NiceValuePipe) {
        const incentivesAndAbsorptionsNegativesValues = [this.summary.incentive < 0, this.summary.ec_plants_rei < 0];
        this.incentivesAndAbsorptionsChart = {
            chart: getChartWithOffsetYAndHeight('donut'),
            colors: [environment.appColors.ecoDarkGreen, environment.appColors.ecoLightGreen],
            dataLabels: disableDataLabels(),
            apexLegends: {
                ...this.getDefaultLegendConfig(),
                formatter(val, opts) {
                    const value = opts.w.globals.series[opts.seriesIndex] as number;
                    const formatted = isDefined(incentivesAndAbsorptionsNegativesValues) &&
                    isDefined(incentivesAndAbsorptionsNegativesValues[opts.seriesIndex]) &&
                    incentivesAndAbsorptionsNegativesValues[opts.seriesIndex] ? -value : value;
                    return val + ' ' + nvp.transform(formatted, '€', 2, formatted < 0);
                }
            },
            plotOptions: getDonutWithSizeAndOffsetX(42, 100),
            labels: getTranslatedLabels(['Incentives', 'Community plants withdrawal'], this.i18n),
            series: [this.summary.incentive, this.summary.ec_plants_rei],
            title: this.mkChartTitle(this.nvp.transform((this.summary.incentive + this.summary.ec_plants_rei), '€', 2),
                environment.appColors.ecoGreen),
            tooltip: getNiceTooltip(nvp)
        };

        const grossRevenue = this.summary.costs + this.summary.net_revenue;
        const expensesAndRevenueNegativesValues = [this.summary.costs < 0, false];
        this.expensesAndRevenueChart = {
            chart: getChartWithOffsetYAndHeight('donut', -70),
            colors: [environment.appColors.ecoRed, environment.appColors.ecoAquaGreen],
            dataLabels: disableDataLabels(),
            apexLegends: {
                ...this.getDefaultLegendConfig(),
                formatter(val, opts) {
                    const value = opts.w.globals.series[opts.seriesIndex] as number;
                    const formatted = isDefined(expensesAndRevenueNegativesValues) &&
                    isDefined(expensesAndRevenueNegativesValues[opts.seriesIndex]) &&
                    expensesAndRevenueNegativesValues[opts.seriesIndex] ? -value : value;
                    return val + ' ' + nvp.transform(formatted, '€', 2, formatted < 0);
                }
            },
            plotOptions: getDonutWithSizeAndOffsetX(42, 100),
            labels: getTranslatedLabels(['Expenses', 'Net revenue'], this.i18n),
            series: [this.summary.costs, Math.abs(this.summary.net_revenue)],
            title: this.mkChartTitle(this.nvp.transform(grossRevenue, '€', 2, grossRevenue < 0),
                environment.appColors.ecoGreen),
            tooltip: getNiceTooltip(nvp)
        };


        // Net revenue graph includes savings fund only if billing period has a yearly interval
        if (this.isWholeYear) {
            const revenue = (this.summary.member_absorption_revenue +
                this.summary.member_plant_revenue + this.summary.third_party_producer_revenue +
                (this.summary.surplus_allocation || 0));
            this.revenueDistributionChart = {
                chart: getChartWithOffsetYAndHeight('pie'),
                colors: [environment.appColors.ecoOrange, environment.appColors.ecoDarkBlue, environment.appColors.ecoLightBlue,
                    environment.appColors.ecoGrey],
                dataLabels: disableDataLabels(),
                apexLegends: {
                    ...this.getDefaultLegendConfig(),
                    formatter(val, opts) {
                        return val + ' ' + nvp.transform(opts.w.globals.series[opts.seriesIndex] as number, '€', 2);
                    }
                },
                plotOptions: getPieWithOffsetX(110),
                labels: getTranslatedLabels(['Members absorption', 'Members plants', 'Third party producers', 'Savings fund'], this.i18n),
                series: [Math.abs(this.summary.member_absorption_revenue),
                    Math.abs(this.summary.member_plant_revenue),
                    Math.abs(this.summary.third_party_producer_revenue),
                    Math.abs(this.summary.surplus_allocation) || 0],
                title: this.mkChartTitle(this.nvp.transform(revenue, '€', 2, revenue < 0),
                    environment.appColors.ecoBlue),
                tooltip: getNiceTooltip(nvp)
            };
        } else {
            const revenue = (this.summary.member_absorption_revenue +
                this.summary.member_plant_revenue + this.summary.third_party_producer_revenue);
            this.revenueDistributionChart = {
                chart: getChartWithOffsetYAndHeight('pie'),
                colors: [environment.appColors.ecoOrange, environment.appColors.ecoDarkBlue,
                    environment.appColors.ecoLightBlue],
                dataLabels: disableDataLabels(),
                apexLegends: {
                    ...this.getDefaultLegendConfig(),
                    formatter(val, opts) {
                        return val + ' ' + nvp.transform(opts.w.globals.series[opts.seriesIndex] as number, '€', 2);
                    }
                },
                plotOptions: getPieWithOffsetX(80),
                labels: getTranslatedLabels(['Members absorption', 'Members plants', 'Third party producers'], this.i18n),
                series: [Math.abs(this.summary.member_absorption_revenue),
                    Math.abs(this.summary.member_plant_revenue),
                    Math.abs(this.summary.third_party_producer_revenue)],
                title: this.mkChartTitle(this.nvp.transform(revenue,
                        '€', 2, revenue < 0),
                    environment.appColors.ecoBlue),
                tooltip: getNiceTooltip(nvp)
            };
        }

        const memberRedistributionRevenue = (this.summary.consumer_absorption_revenue +
            this.summary.prosumer_absorption_revenue);
        const negativesValues = [this.summary.consumer_absorption_revenue < 0, this.summary.prosumer_absorption_revenue < 0];
        this.membersRedistributionChart = {
            chart: getChartWithOffsetYAndHeight('donut'),
            colors: [environment.appColors.ecoDarkGreen, environment.appColors.ecoLightGreen],
            dataLabels: disableDataLabels(),
            apexLegends: {
                ...this.getDefaultLegendConfig(),
                formatter(val, opts) {
                    const value = opts.w.globals.series[opts.seriesIndex] as number;
                    const formatted = isDefined(negativesValues) && isDefined(negativesValues[opts.seriesIndex]) && negativesValues[opts.seriesIndex] ? -value : value;
                    return val + ' ' + nvp.transform(formatted, '€', 2, formatted < 0);
                }
            },
            plotOptions: getDonutWithSizeAndOffsetX(42, 80),
            labels: getTranslatedLabels(['Consumers', 'Prosumers'], this.i18n),
            series: [Math.abs(this.summary.consumer_absorption_revenue),
                Math.abs(this.summary.prosumer_absorption_revenue)],
            title: this.mkChartTitle(this.nvp.transform(memberRedistributionRevenue, '€', 2, memberRedistributionRevenue < 0),
                environment.appColors.ecoGreen),
            tooltip: getNiceTooltip(nvp)
        };

        this.sharedFromAbsorption = {
            chart: {type: 'donut', height: 120},
            colors: [environment.appColors.ecoYellow, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: getDonutWithSize(),
            labels: getTranslatedLabels(['Shared energy', 'Energy not shared'], this.i18n),
            series: [this.summary.absorption_share_score * 100, (1 - this.summary.absorption_share_score) * 100],
            tooltip: getNiceTooltip(nvp)
        };

        this.sharedFromInjection = {
            chart: {type: 'donut', height: 120},
            colors: [environment.appColors.ecoDarkGreen, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: getDonutWithSize(),
            labels: getTranslatedLabels(['Shared energy', 'Energy not shared'], this.i18n),
            series: [this.summary.injection_share_score * 100, (1 - this.summary.injection_share_score) * 100],
            tooltip: getNiceTooltip(nvp)
        };
    }

    private getDefaultLegendConfig(): ApexLegend {
        return {
            floating: true,
            fontFamily: environment.fontFamily,
            fontSize: '12px',
            fontWeight: '400',
            labels: {
                colors: Array(4).fill(environment.appColors.ecoLegend)
            },
            markers: {
                width: 16,
                height: 16,
                radius: 4,
                offsetY: 3,
                offsetX: -5
            },
            offsetY: 60,
            position: 'left'
        };
    }

    private mkChartTitle(text: string, color: string, offsetY?: number): ApexTitleSubtitle {
        return {
            offsetY: offsetY || 30,
            style: {
                fontSize: '28px',
                fontWeight: '700',
                fontFamily: environment.fontFamily,
                color
            },
            text
        };
    }

    private generateInactiveEcoSummaryData() {
        this.summary = {
            months: {} as ECEconomicsSummaryMonth[],
            net_revenue: 0,
            member_absorption_revenue: 0,
            member_plant_revenue: 0,
            third_party_producer_revenue: 0,
            incentive: 0,
            ec_plants_rei: 0,
            costs: 0,
            consumer_absorption_revenue: 0,
            prosumer_absorption_revenue: 0,
            absorption_share_performance: {} as ECEconomicsPerformance,
            absorption_share_score: 0,
            injection_share_performance: {} as ECEconomicsPerformance,
            injection_share_score: 0,
            ec_plants_count: 0,
            member_plants_count: 0,
            third_party_plants_count: 0,
            errors: []
        };
    }

}



